/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Controller, type FieldPath, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Select } from '@/shared/ui/components/form/inputs';
import type { PropertyFormValues } from './schema';

/**
 * Reduces whatever the select emits to one stored key, or undefined when cleared.
 *
 * @param next - A value, an array (multi mode), null or an empty string.
 * @returns The key, or undefined for "not informed".
 */
export function selectedKey(next: string | string[] | null | undefined): string | undefined {
  const value = Array.isArray(next) ? next[0] : next;
  return value ? value : undefined;
}

interface EnumSelectFieldProps {
  form: UseFormReturn<PropertyFormValues>;
  name: FieldPath<PropertyFormValues>;
  label: string;
  /** Stored keys, in display order. */
  options: readonly string[];
  /** i18n prefix of the labels: `${labelPrefix}.${key}`. */
  labelPrefix: string;
}

/**
 * Select of an English-keyed enum with translated labels. Clearable, since
 * every Univen attribute is optional and empty means "not informed".
 *
 * @param props - Form, field, label and the enum keys.
 * @returns The select field.
 */
export function EnumSelectField({ form, name, label, options, labelPrefix }: EnumSelectFieldProps) {
  const { t } = useTranslation('features/properties');
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <Select
          label={label}
          placeholder={t('form.drawer.selectPlaceholder')}
          clearable
          value={typeof field.value === 'string' ? field.value : ''}
          onChange={(next) => field.onChange(selectedKey(next))}
          data={options.map((key) => ({ value: key, label: t(`${labelPrefix}.${key}`) }))}
        />
      )}
    />
  );
}
